import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * Grouped settings lists in the style of Apple's Settings and Apple Account pages: a small caption
 * over a white rounded group, rows split by hairlines that stop short of the left edge, a short note
 * under the group, switches that apply the moment they are flipped, and chevrons on rows that open
 * something. Every row is at least a thumb tall.
 */
export const SETTINGS_CSS = `
.st-page { display: grid; gap: 28px; }
.st-group { display: grid; gap: 6px; }
.st-caption { margin: 0; padding: 0 16px; font-size: 13px; font-weight: 600; letter-spacing: .04em;
  text-transform: uppercase; color: var(--ink-soft); }
.st-list { background: var(--paper); border-radius: 16px; overflow: hidden;
  box-shadow: 0 1px 2px rgba(20,20,20,.04), 0 0 0 1px rgba(20,20,20,.05); }
.st-note { margin: 0; padding: 0 16px; font-size: 13px; line-height: 1.45; color: var(--ink-soft); }
.st-note a { color: var(--ink); font-weight: 600; text-underline-offset: 3px; }
.st-note[data-error] { color: var(--crave-red); }

.st-row { position: relative; display: flex; align-items: center; gap: 12px; min-height: 52px;
  padding: 10px 16px; color: inherit; text-decoration: none; background: none; border: 0; width: 100%;
  text-align: left; font: inherit; }
/* Hairlines between rows that start past the left padding, like iOS. A background image, so a row
   can still take a highlight colour underneath it, and it works on details elements too. */
.st-list > * + * { background-image: linear-gradient(var(--line), var(--line));
  background-size: calc(100% - 16px) 1px; background-position: right top; background-repeat: no-repeat; }
a.st-row, button.st-row, summary.st-row { cursor: pointer; }
a.st-row:active, button.st-row:active, summary.st-row:active { background: var(--mist); }
@media (hover: hover) { a.st-row:hover, button.st-row:hover, summary.st-row:hover { background: #fbf9f5; } }
.st-row-main { flex: 1; min-width: 0; display: grid; gap: 1px; }
.st-row-label { font-weight: 500; line-height: 1.3; }
.st-row-detail { font-size: 14px; line-height: 1.35; color: var(--ink-soft); overflow-wrap: anywhere; }
.st-row-value { color: var(--ink-soft); text-align: right; white-space: nowrap; }
.st-row[data-tone='danger'] { color: var(--crave-red); justify-content: center; font-weight: 600; }
.st-row[data-tone='center'] { justify-content: center; font-weight: 600; }
.st-chevron { flex: none; width: 8px; height: 13px; color: #b8b2a8; }
details[open] > summary .st-chevron { transform: rotate(90deg); }
summary.st-row { list-style: none; }
summary.st-row::-webkit-details-marker { display: none; }
.st-block { padding: 14px 16px 16px; display: grid; gap: 12px; }

.st-input-row { display: flex; align-items: center; gap: 12px; min-height: 52px; padding: 0 16px; }
.st-input-row label { flex: none; width: 34%; max-width: 140px; font-weight: 500; }
.st-input-row input { flex: 1; min-width: 0; min-height: 44px; border: 0; background: transparent;
  text-align: right; color: var(--ink); font-size: 16px; padding: 0; }
.st-input-row input::placeholder { color: #b0aaa0; }
.st-input-row input:focus-visible { outline: none; }
.st-input-row:focus-within { background: #fbf9f5; }

.st-switch { flex: none; position: relative; width: 51px; height: 31px; border-radius: 999px; border: 0;
  padding: 0; background: #e3dfd8; cursor: pointer; transition: background .2s ease; }
.st-switch::after { content: ''; position: absolute; top: 2px; left: 2px; width: 27px; height: 27px;
  border-radius: 50%; background: #fff; box-shadow: 0 3px 8px rgba(0,0,0,.15), 0 1px 1px rgba(0,0,0,.16);
  transition: transform .2s ease; }
.st-switch[aria-checked='true'] { background: var(--crave-red); }
.st-switch[aria-checked='true']::after { transform: translateX(20px); }
.st-switch:disabled { opacity: .5; cursor: default; }
/* The switch is 31px tall; its tap area is the full row height. */
.st-switch::before { content: ''; position: absolute; inset: -7px -4px; }

.st-pill { display: inline-flex; align-items: center; height: 22px; padding: 0 9px; border-radius: 999px;
  font-size: 12px; font-weight: 600; background: var(--mist); color: var(--ink-soft); white-space: nowrap; }
.st-pill[data-tone='live'] { background: #fdecec; color: var(--crave-red); }
.st-pill[data-tone='done'] { background: #e9f3ed; color: var(--ok); }
.st-capsule { display: inline-flex; align-items: center; min-height: 44px; padding: 0 2px; border: 0;
  background: none; color: var(--crave-red); font: inherit; font-size: 15px; font-weight: 600; cursor: pointer;
  text-decoration: none; }
.st-capsule:disabled { opacity: .5; cursor: default; }
`;

export function Group({
  title,
  note,
  noteError,
  children,
}: {
  title?: string | undefined;
  note?: ReactNode;
  noteError?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <section className="st-group">
      {title && <h2 className="st-caption">{title}</h2>}
      <div className="st-list">{children}</div>
      {note && (
        <p
          className="st-note"
          data-error={noteError ? '' : undefined}
          role={noteError ? 'alert' : undefined}
        >
          {note}
        </p>
      )}
    </section>
  );
}

export function Chevron() {
  return (
    <svg className="st-chevron" viewBox="0 0 8 13" aria-hidden="true">
      <path
        d="M1.5 1.5 6.5 6.5l-5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface RowText {
  label: ReactNode;
  detail?: ReactNode;
  value?: ReactNode;
}

function RowBody({ label, detail, value }: RowText) {
  return (
    <>
      <span className="st-row-main">
        <span className="st-row-label">{label}</span>
        {detail && <span className="st-row-detail">{detail}</span>}
      </span>
      {value !== undefined && <span className="st-row-value">{value}</span>}
    </>
  );
}

/** A plain row, or one that opens something when it has `to`. */
export function Row({ to, trailing, ...text }: RowText & { to?: string; trailing?: ReactNode }) {
  if (to)
    return (
      <Link to={to} className="st-row">
        <RowBody {...text} />
        {trailing}
        <Chevron />
      </Link>
    );
  return (
    <div className="st-row">
      <RowBody {...text} />
      {trailing}
    </div>
  );
}

/** A whole-row button, like Sign Out. */
export function ActionRow({
  children,
  tone = 'center',
  onClick,
  disabled,
}: {
  children: ReactNode;
  tone?: 'center' | 'danger';
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button type="button" className="st-row" data-tone={tone} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export function SwitchRow({
  checked,
  onChange,
  disabled,
  ...text
}: RowText & { checked: boolean; onChange: (next: boolean) => void; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="st-row">
      <span className="st-row-main" id={id}>
        <span className="st-row-label">{text.label}</span>
        {text.detail && <span className="st-row-detail">{text.detail}</span>}
      </span>
      <button
        type="button"
        role="switch"
        className="st-switch"
        aria-checked={checked}
        aria-labelledby={id}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      />
    </div>
  );
}

/** Label on the left, the value typed in place on the right, as in Apple's contact cards. */
export function InputRow({
  label,
  id,
  ...input
}: { label: string; id: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="st-input-row">
      <label htmlFor={id}>{label}</label>
      <input id={id} {...input} />
    </div>
  );
}

/** A row that opens in place to show a small form. */
export function DisclosureRow({ children, ...text }: RowText & { children: ReactNode }) {
  return (
    <details>
      <summary className="st-row">
        <RowBody {...text} />
        <Chevron />
      </summary>
      <div className="st-block">{children}</div>
    </details>
  );
}
